/**
 * AdMesh Viewability Tracker Component
 * Wraps any ad component with MRC viewability tracking
 */

import { useRef, useEffect, type CSSProperties, type FC, type ReactNode, type RefObject } from 'react';
import { logger } from '../utils/logger';
import { useViewabilityTracker } from '../hooks/useViewabilityTracker';
import type { ViewabilityTrackerConfig } from '../types/analytics';

export interface AdMeshViewabilityTrackerProps {
  /** Product ID */
  productId?: string;
  /** Offer ID */
  offerId?: string;
  /** Agent ID */
  agentId?: string;
  /** Recommendation ID (for exposure tracking) */
  recommendationId: string;
  /** Exposure URL (for MRC-compliant exposure pixel firing) */
  exposureUrl?: string;
  /** Session ID (for exposure tracking) */
  sessionId?: string;
  /** Whether to fire exposure URL when ad becomes viewable (defaults to true) */
  triggerExposureUrl?: boolean;
  /** Children to wrap with viewability tracking */
  children: ReactNode;
  /** Custom viewability tracker configuration */
  config?: Partial<ViewabilityTrackerConfig>;
  /** CSS class name */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
  /** Callback when viewability state changes */
  onViewabilityChange?: (isViewable: boolean) => void;
  /** Callback when ad becomes visible */
  onVisible?: () => void;
  /** Callback when ad becomes viewable (meets MRC threshold) */
  onViewable?: () => void;
  /** Callback when ad is clicked */
  onClick?: () => void;
}

/**
 * AdMeshViewabilityTracker Component
 * 
 * Wraps ad components with comprehensive MRC viewability tracking.
 * Automatically tracks:
 * - Viewability (50% visible for 1 second)
 * - Time metrics (time to viewable, total visible duration, etc.)
 * - Engagement metrics (hover, focus, clicks, scroll depth)
 * - Context metrics (device type, viewport size, ad position)
 * 
 * @example
 * ```tsx
 * <AdMeshViewabilityTracker
 *   recommendationId="rec_123"
 *   productId="prod_456"
 *   offerId="offer_789"
 *   onViewable={() => logger.log('Ad is viewable!')}
 * >
 *   <YourAdComponent />
 * </AdMeshViewabilityTracker>
 * ```
 */
export const AdMeshViewabilityTracker: FC<AdMeshViewabilityTrackerProps> = ({
  productId,
  offerId,
  agentId,
  recommendationId,
  exposureUrl,
  sessionId,
  triggerExposureUrl = true,
  children,
  config,
  className,
  style,
  onViewabilityChange,
  onVisible,
  onViewable,
  onClick
}) => {
  const elementRef = useRef<HTMLElement>(null);
  const exposureFired = useRef(false);

  // Use viewability tracker hook
  const viewabilityState = useViewabilityTracker({
    productId,
    offerId,
    agentId,
    recommendationId,
    elementRef: elementRef as RefObject<HTMLElement>,
    config
  });

  // Track viewability changes and fire exposure pixel
  const previousViewable = useRef(viewabilityState.isViewable);

  useEffect(() => {
    exposureFired.current = false;
    previousViewable.current = false;
  }, [exposureUrl, recommendationId, sessionId]);

  useEffect(() => {
    if (viewabilityState.isViewable !== previousViewable.current) {
      previousViewable.current = viewabilityState.isViewable;

      if (onViewabilityChange) {
        onViewabilityChange(viewabilityState.isViewable);
      }

      if (viewabilityState.isViewable && onViewable) {
        onViewable();
      }

      // Fire exposure pixel when ad becomes viewable (MRC-compliant)
      // Only fire if we have the required data and haven't fired yet
      if (viewabilityState.isViewable && !exposureFired.current && triggerExposureUrl) {
        logger.log('[AdMeshViewabilityTracker] 🎯 Ad is viewable, checking exposure pixel requirements:', {
          exposureUrl: exposureUrl ? 'present' : 'MISSING',
          sessionId: sessionId ? 'present' : 'MISSING',
          recommendationId
        });

        if (exposureUrl && sessionId) {
          exposureFired.current = true;

          logger.log('[AdMeshViewabilityTracker] 🔥 Firing exposure pixel:', exposureUrl);

          // Fire the exposure pixel using fetch with keepalive
          fetch(exposureUrl, { method: 'GET', keepalive: true })
            .then(() => {
              logger.log('[AdMesh] ✅ Exposure pixel fired successfully');
            })
            .catch((error) => {
              logger.warn('[AdMesh] ⚠️ Failed to fire exposure pixel:', error);
              // Reset flag to allow retry
              exposureFired.current = false;
            });
        } else {
          logger.warn('[AdMeshViewabilityTracker] ⚠️ Cannot fire exposure pixel - missing required data:', {
            hasExposureUrl: !!exposureUrl,
            hasSessionId: !!sessionId
          });
        }
      }
    }
  }, [viewabilityState.isViewable, onViewabilityChange, onViewable, exposureUrl, sessionId, recommendationId, triggerExposureUrl]);

  // Track visibility changes
  const previousVisible = useRef(viewabilityState.isVisible);
  
  useEffect(() => {
    if (viewabilityState.isVisible !== previousVisible.current) {
      previousVisible.current = viewabilityState.isVisible;
      
      if (viewabilityState.isVisible && onVisible) {
        onVisible();
      }
    }
  }, [viewabilityState.isVisible, onVisible]);

  // Handle click
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    
    // Allow event to propagate to children
  };

  return (
    <div
      ref={elementRef as RefObject<HTMLDivElement>}
      className={className}
      style={style}
      onClick={handleClick}
      data-admesh-viewability-tracker
      data-recommendation-id={recommendationId}
      data-is-viewable={viewabilityState.isViewable}
      data-is-visible={viewabilityState.isVisible}
      data-visibility-percentage={viewabilityState.visibilityPercentage.toFixed(2)}
    >
      {children}
    </div>
  );
};

AdMeshViewabilityTracker.displayName = 'AdMeshViewabilityTracker';
